/**
 * ServiceOrderChecklistsTab - Aba Checklists do modal da OS (Fase 8)
 *
 * Mostra as etapas Início → Execução → Conclusão. Cada checklist abre o
 * modal oficial da Vistoria (`InspectionDialog`), que já traz execução,
 * link externo, aceites/assinaturas do Core de Aceites e revisão.
 *
 * CHANGELOG:
 *   RELEASE: 2026-10-10 v1.4.2 - Aviso "Checklist ainda não gerado" com a mesma largura
 *            reduzida (80%) do cartão do checklist em telas largas.
 *   RELEASE: 2026-10-10 v1.4.1 - Cartão do checklist 20% mais estreito em telas largas e
 *            barra de progresso verde quando o checklist está completo ou aprovado.
 *   RELEASE: 2026-10-10 v1.4.0 - BR-OS-033: botão manual "Gerar checklist" para etapa
 *            ativa sem checklist gerado (somente esta O.S, sem alteração em massa).
 *   RELEASE: 2026-10-10 v1.3.1 - Prazo, barra e contagem de itens passam para a
 *            mesma linha do código e da situação do checklist (só apresentação).
 *   RELEASE: 2026-10-10 v1.3.0 - Aproxima a timeline da referência aprovada:
 *            etapas ficam sobre a linha e somente instâncias geradas usam cartões recuados.
 *   RELEASE: 2026-10-10 v1.2.0 - Exibe prazo e progresso por itens em cada
 *            checklist da Central Operacional.
 *   RELEASE: 2026-10-10 v1.1.1 - Corrige a linha do tempo para conectar somente
 *            etapas exibidas e aplica elevação premium ao resumo de progresso.
 *   RELEASE: 2026-10-10 v1.1.0 - Novo leiaute Central Operacional com resumo
 *            de progresso e etapas conectadas, sem alterar regras ou ações.
 *   RELEASE: 2026-10-08 v1.0.0 - Criação inicial.
 */
import { SERVICE_ORDER_STARTABLE, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, ClipboardCheck, Lock, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { PREMIUM_DASHBOARD_CARD } from '@/components/dashboard/shared/dashboardCardStyles';
import { InspectionDialog } from '@/components/checklist/inspection/InspectionDialog';
import { CHECKLIST_STAGES } from '@/hooks/useServiceTypes';
import {
  computeStageStates,
  type ServiceOrderChecklistRow,
  useServiceOrderChecklists,
} from '@/hooks/useServiceOrderChecklists';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente', in_progress: 'Em andamento', awaiting_review: 'Aguardando revisão',
  correction_requested: 'Correção solicitada', approved: 'Aprovado', rejected: 'Reprovado', cancelled: 'Cancelado',
};

const STATUS_CLASS: Record<string, string> = {
  pending: 'bg-warning/10 text-warning',
  in_progress: 'bg-info/10 text-info',
  awaiting_review: 'bg-info/10 text-info',
  correction_requested: 'bg-warning/10 text-warning',
  approved: 'bg-success/10 text-success',
  rejected: 'bg-destructive/10 text-destructive',
  cancelled: 'bg-muted text-muted-foreground',
};

/** Resumo derivado apenas dos dados já retornados pela RPC da aba. */
export function summarizeServiceOrderChecklists(rows: ServiceOrderChecklistRow[]) {
  const completed = rows.filter((row) => row.instance_status === 'approved').length;
  const inProgress = rows.filter((row) => ['in_progress', 'awaiting_review', 'correction_requested'].includes(row.instance_status ?? '')).length;
  const pending = rows.length - completed - inProgress;
  const progress = rows.length > 0 ? Math.round((completed / rows.length) * 100) : 0;
  return { completed, inProgress, pending, total: rows.length, progress };
}

/** Deriva o percentual sem permitir valores inválidos ou acima de 100%. */
export function calculateChecklistItemProgress(itemsAnswered: number, itemsTotal: number) {
  if (itemsTotal <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round((itemsAnswered / itemsTotal) * 100)));
}

/**
 * v1.4.1: define quando a barra de progresso deve ficar verde.
 * Vale tanto para o checklist com todos os itens respondidos quanto para o
 * já aprovado, evitando o tom escuro padrão do indicador.
 */
export function isChecklistProgressComplete(itemsAnswered: number, itemsTotal: number, instanceStatus: string | null) {
  if (instanceStatus === 'approved') return true;
  return itemsTotal > 0 && calculateChecklistItemProgress(itemsAnswered, itemsTotal) === 100;
}

/** Formata o prazo na mesma leitura curta utilizada pelo modal de vistoria. */
function formatChecklistDueAt(value: string | null) {
  if (!value) return 'Livre';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

/** Identifica somente situações em que o prazo final já foi ultrapassado. */
function isChecklistOverdue(deadlineStatus: string | null) {
  return deadlineStatus === 'finish_overdue' || deadlineStatus === 'closed_overdue';
}

/** Rótulo compacto exibido junto à etapa da timeline. */
export function getChecklistStageStateLabel(state: 'locked' | 'active' | 'done' | null) {
  if (state === 'done') return 'Concluída';
  if (state === 'locked') return 'Bloqueada';
  return 'Ativa';
}

/** BR-OS-033: etapa ativa cujo checklist configurado ainda não foi gerado. */
export function isStagePendingGeneration(state: 'locked' | 'active' | 'done' | null, stageRows: ServiceOrderChecklistRow[]) {
  return state === 'active' && stageRows.length > 0 && stageRows.some((row) => !row.instance_id);
}

export function ServiceOrderChecklistsTab({ serviceOrderId, orderStatus, canReleaseStage = false, onReleaseStage }: {
  serviceOrderId: string;
  orderStatus: string;
  canReleaseStage?: boolean;
  onReleaseStage?: () => Promise<unknown>;
}) {
  const qc = useQueryClient();
  const { data, isLoading, error } = useServiceOrderChecklists(serviceOrderId);
  const [openId, setOpenId] = useState<string | null>(null);
  const [releasing, setReleasing] = useState(false);

  /** Gera manualmente só a etapa pendente desta O.S (servidor revalida tudo). */
  const handleRelease = async () => {
    if (!onReleaseStage) return;
    setReleasing(true);
    try {
      await onReleaseStage();
      toast.success('Checklist gerado');
    } catch (e) {
      toast.error((e as Error).message || 'Não foi possível gerar o checklist');
    } finally {
      setReleasing(false);
      qc.invalidateQueries({ queryKey: ['service-order-checklists', serviceOrderId] });
    }
  };

  if (isLoading) return <div className="p-4"><Skeleton className="h-32" /></div>;
  if (error) return <div className="p-4"><InlineNotice variant="error">{(error as Error).message}</InlineNotice></div>;
  const rows = data ?? [];
  if (rows.length === 0) {
    return <div className="p-4"><InlineNotice variant="info">O tipo de serviço desta OS não tem checklists configurados.</InlineNotice></div>;
  }
  const states = computeStageStates(rows);
  const summary = summarizeServiceOrderChecklists(rows);
  const visibleStages = CHECKLIST_STAGES.filter((stage) => states[stage.value]);

  return (
    <div className="space-y-4 p-2 md:p-4">
      {SERVICE_ORDER_STARTABLE.includes(orderStatus as ServiceOrderStatus) && (
        <InlineNotice variant="info">Os checklists são criados ao Iniciar a OS. Cada etapa só libera depois que a anterior for aprovada.</InlineNotice>
      )}

      <section aria-label="Progresso dos checklists" className={cn(PREMIUM_DASHBOARD_CARD, 'rounded-lg border p-3 md:p-4')}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
          <div className="flex shrink-0 items-center gap-3 lg:min-w-44">
            <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-[6px] border-info/15 text-lg font-bold text-info">
              {summary.progress}%
            </div>
            <div>
              <p className="text-xs font-semibold uppercase text-muted-foreground">Progresso</p>
              <p className="text-sm font-medium text-foreground">dos checklists</p>
            </div>
          </div>
          <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              { label: 'Concluídos', value: summary.completed, className: 'text-success' },
              { label: 'Em andamento', value: summary.inProgress, className: 'text-info' },
              { label: 'Pendentes', value: summary.pending, className: 'text-warning' },
              { label: 'Total', value: summary.total, className: 'text-foreground' },
            ].map((item) => (
              <div key={item.label} className="flex min-h-16 flex-col items-center justify-center border-l first:border-l-0">
                <span className={cn('text-xl font-semibold', item.className)}>{item.value}</span>
                <span className="text-center text-xs text-muted-foreground">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="space-y-3">
        {visibleStages.map((s, index) => {
          const state = states[s.value];
          const stageRows = rows.filter((row) => row.stage === s.value);
          const generatedRows = stageRows.filter((row) => row.instance_id);
          const hasNextStage = index < visibleStages.length - 1;
          return (
            <div key={s.value} className={cn('relative grid min-h-24 grid-cols-[2.5rem_minmax(0,1fr)] gap-2 md:grid-cols-[3.25rem_minmax(0,1fr)] md:gap-3', hasNextStage && 'pb-8')}>
              <div className="relative flex justify-center pt-0.5">
                {hasNextStage && <div aria-hidden className="absolute bottom-[-2rem] top-9 w-px bg-border" />}
                <div className={cn(
                  'relative z-10 flex h-9 w-9 items-center justify-center rounded-full border-2 bg-background',
                  state === 'active' && 'border-info text-info shadow-sm',
                  state === 'done' && 'border-success bg-success text-success-foreground',
                  state === 'locked' && 'border-border text-muted-foreground',
                )}>
                  {state === 'done' ? <CheckCircle2 className="h-4 w-4" /> : state === 'locked'
                    ? <Lock className="h-3.5 w-3.5" /> : <PlayCircle className="h-4 w-4" />}
                </div>
              </div>
              <section className="min-w-0">
                <header className="flex min-h-10 flex-wrap items-center gap-2 pb-3">
                  <h3 className={cn('text-base font-semibold', state === 'locked' && 'text-muted-foreground')}>{s.label}</h3>
                  <span className={cn(
                    'rounded-full px-2.5 py-1 text-[10px] font-semibold',
                    state === 'active' && 'bg-info/10 text-info',
                    state === 'done' && 'bg-success/10 text-success',
                    state === 'locked' && 'bg-muted text-muted-foreground',
                  )}>
                    {getChecklistStageStateLabel(state)}
                  </span>
                </header>
                {generatedRows.length > 0 ? (
                <div className="space-y-2 md:ml-1">
                  {generatedRows.map((row) => {
                    const itemProgress = calculateChecklistItemProgress(row.items_answered, row.items_total);
                    const overdue = isChecklistOverdue(row.deadline_status);
                    const progressComplete = isChecklistProgressComplete(row.items_answered, row.items_total, row.instance_status);
                    return (
                    /*
                      v1.4.1: em telas largas (modal aberto por completo) o cartão
                      ocupa 80% da coluna, reduzindo a largura em 20% sem cortar
                      nenhum dado; em telas estreitas segue em largura total.
                    */
                    <div key={row.template_id} className={cn(
                      'flex flex-col gap-3 rounded-lg border bg-card px-3 py-3 sm:flex-row sm:items-center md:max-w-[80%] md:px-4',
                      state === 'active' && 'border-info/50 shadow-sm',
                      state === 'locked' && 'bg-muted/30',
                    )}>
                      <div className={cn(
                        'flex h-11 w-11 shrink-0 items-center justify-center rounded-full border',
                        state === 'active' ? 'border-info/20 bg-info/10 text-info' : state === 'done' ? 'border-success/20 bg-success/10 text-success' : 'text-muted-foreground',
                      )}>
                        <ClipboardCheck className="h-5 w-5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={cn('truncate text-sm font-semibold', state === 'locked' && 'text-muted-foreground')}>{row.template_name}</p>
                        {/*
                          v1.3.1: código, situação, barra de progresso, contagem de
                          itens e prazo compartilham uma única linha (BR-OS-005).
                          Em telas estreitas os itens quebram para a linha seguinte
                          sem perder nenhum dado.
                        */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                          {row.instance_id ? (
                            <>
                              <span className="shrink-0 text-muted-foreground">#{row.instance_code}</span>
                              <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', STATUS_CLASS[row.instance_status ?? ''] ?? 'bg-muted text-muted-foreground')}>
                                {STATUS_LABEL[row.instance_status ?? ''] ?? row.instance_status}
                              </span>
                              <div className="flex min-w-[9rem] flex-1 items-center gap-2">
                                <Progress
                                  value={itemProgress}
                                  className="h-1.5 min-w-16 flex-1"
                                  indicatorClassName={progressComplete ? 'bg-success' : undefined}
                                />
                                <span className="shrink-0 whitespace-nowrap font-medium tabular-nums text-foreground">
                                  {row.items_answered}/{row.items_total} itens
                                </span>
                              </div>
                              <span className="shrink-0 whitespace-nowrap text-muted-foreground">
                                Prazo <span className={cn('font-medium', overdue ? 'text-destructive' : 'text-foreground')}>{formatChecklistDueAt(row.due_at)}</span>
                              </span>
                            </>
                          ) : <span className="text-muted-foreground">Não gerado</span>}
                        </div>
                      </div>
                      {row.instance_id && (
                        <Button size="xs" variant={state === 'active' ? 'default' : 'outline'} onClick={() => setOpenId(row.instance_id)} className="w-full gap-1.5 sm:w-auto">
                          Abrir <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                    );
                  })}
                </div>
                ) : null}
                {isStagePendingGeneration(state, stageRows) && (
                  /* v1.4.2: o aviso de checklist não gerado segue a mesma largura
                     reduzida (80%) do cartão, mantendo o alinhamento das duas caixas. */
                  <div className="flex flex-col gap-2 rounded-lg border border-dashed px-3 py-3 sm:flex-row sm:items-center md:ml-1 md:max-w-[80%] md:px-4">
                    <p className="flex-1 text-xs text-muted-foreground">Checklist ainda não gerado para esta etapa.</p>
                    {canReleaseStage && orderStatus === 'in_progress' && (
                      <Button size="xs" onClick={handleRelease} disabled={releasing} className="w-full gap-1.5 sm:w-auto">
                        <ClipboardCheck className="h-3.5 w-3.5" /> {releasing ? 'Gerando...' : 'Gerar checklist'}
                      </Button>
                    )}
                  </div>
                )}
              </section>
            </div>
          );
        })}
      </div>
      {openId && (
        <InspectionDialog instanceId={openId} onOpenChange={(o: boolean) => {
          if (!o) {
            setOpenId(null);
            // Aprovação pode liberar a próxima etapa no servidor
            qc.invalidateQueries({ queryKey: ['service-order-checklists', serviceOrderId] });
          }
        }} />
      )}
    </div>
  );
}
